import React, { useCallback, useEffect, useRef, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import ReactTooltip from "react-tooltip";
import s from "./styles.scss";
import IUser from "../../../types/services/IUser";
import { searchDonorsByIdsExtended } from "../../../utils/fetch";

interface Props {
  lineClassName?: string;
  donorIds: string[];
  charityId?: string;
  searchUrl?: string;
  token?: string;
  tooltipId: string;
  strongLabel?: boolean;
  label?: string;
  inline?: boolean;
}

function donorDisplayName(donor: IUser): string {
  const name = (donor.name || "").trim();
  if (name) {
    return name;
  }
  const company = (donor.company || "").trim();
  if (company) {
    return company;
  }
  return donor.id;
}

const SpecificContactsLabel: React.FC<Props> = ({
  lineClassName,
  donorIds,
  charityId,
  searchUrl,
  token,
  tooltipId,
  strongLabel,
  label = "Specific Contacts",
  inline,
}) => {
  const [names, setNames] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const started = useRef(false);

  const loadNames = useCallback(() => {
    if (started.current) {
      return;
    }
    if (!searchUrl || !charityId || !token || donorIds.length === 0) {
      setLoadError(true);
      return;
    }
    started.current = true;
    setLoadError(false);
    setLoading(true);
    searchDonorsByIdsExtended(searchUrl, charityId, donorIds, token)
      .then(donors => {
        const found = new Set(
          (donors || []).map(donor => donor && donor.id).filter(Boolean)
        );
        const labels = (donors || [])
          .filter(donor => donor)
          .map(donorDisplayName);
        donorIds.forEach(id => {
          if (!found.has(id)) {
            labels.push(id);
          }
        });
        setNames(labels);
        setLoading(false);
      })
      .catch(() => {
        started.current = false;
        setLoadError(true);
        setLoading(false);
      });
  }, [charityId, donorIds, searchUrl, token]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [names, loading, loadError]);

  const count = donorIds.length;
  const title = strongLabel ? <strong>{label}</strong> : <>{label}</>;
  const tooltip = (
    <ReactTooltip
      id={tooltipId}
      type="info"
      place="bottom"
      effect="float"
      delayHide={200}
    >
      {loading && names === null && !loadError && <span>Loading…</span>}
      {loadError && <span>Could not load contact names</span>}
      {names &&
        names.map((name, index) => (
          <div key={`${name}-${index}`}>{name}</div>
        ))}
    </ReactTooltip>
  );
  const countMark = (
    <strong
      className={s.count}
      data-tip
      data-for={tooltipId}
      onMouseEnter={loadNames}
    >
      ({count})
    </strong>
  );

  if (inline) {
    return (
      <span>
        {title} {countMark}
        {tooltip}
        <br />
      </span>
    );
  }

  return (
    <p className={lineClassName}>
      {title} {countMark}
      {tooltip}
    </p>
  );
};

export default withStyles(s)(SpecificContactsLabel);
